'use client';

import { useContext, useMemo, useState, type FC } from 'react';
import {
  ConfirmRemoveDocument,
  Tooltip,
  Text,
  Button,
  ButtonGroup,
  Flex,
  UploadLaterDialog,
} from '@/_components';
import {
  PREDEFINED_PROVIDED_ELSEWHERE,
  PREDEFINED_NOT_APPLICABLE,
} from '@/_constants/questions';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { NotesButton } from '../NotesButton';
import type { QuestionActionsUploadProps } from './QuestionActionsUpload.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserContext } from '@/_contexts/UserProvider';
import {
  deletePath,
  downloadAttachedDocument,
} from '@/_api/client-questionnaires';
import { getUploadedItems } from '@/_utils/answers';
import { getIsPastDate } from '@/_utils/dates';
import { Question, Questionnaire } from '@/_types/models/questionnaire';
import { ToastContext } from '@/_contexts/ToastProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';

export const QuestionActionsUpload: FC<QuestionActionsUploadProps> = ({
  question,
  attached_document,
  mode,
  isDisabled,
}) => {
  const { updateQuestion, selectedQuestionnaire, setSelectedQuestion } =
    useContext(ClientQuestionnaireContext);
  const {
    previewCompanyId,
    updatePreviewQuestion,
    previewQuestionnaire,
    setSelectedPreviewQuestion,
  } = useContext(PreviewContext);
  const { token, selectedCompanyId } = useContext(UserContext);
  const { previewTemplate, updateTemplateQuestion } =
    useContext(UserPreviewContext);
  const { addToast } = useContext(ToastContext);

  const questionnaire: Questionnaire | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestionnaire;
    if (mode === 'admin-template') return previewTemplate as any;

    return selectedQuestionnaire;
  }, [mode, selectedQuestionnaire, previewTemplate, previewQuestionnaire]);

  const companyId: number | null | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewCompanyId;
    if (mode === 'admin-template') return -1;

    return selectedCompanyId;
  }, [mode, previewCompanyId, selectedCompanyId]);

  const { preferences } = useContext(FirmSettingsContext);

  const isPastDue: boolean = getIsPastDate(questionnaire?.due_date);

  const [isUploadLaterDialogOpen, setIsUploadLaterDialogOpen] = useState(false);
  const [
    isDocumentDeletionCheckDialogOpened,
    setIsDocumentDeletionCheckDialogOpened,
  ] = useState(false);
  const [isProvidingElsewhere, setIsProvidingElsewhere] = useState(false);
  const [isUploadingLater, setIsUploadingLater] = useState(false);

  const isUploadLater: boolean = !!question?.answer_by;
  const isProvidedElsewhere: boolean =
    question?.answer === PREDEFINED_PROVIDED_ELSEWHERE;

  const hasAttachedDocument: boolean = attached_document !== '';

  const updateAnswer = async (questionId: number, answer: string) => {
    const answerParams = {
      questionId,
      answer,
    };

    if (mode === 'admin-preview') return updatePreviewQuestion(answerParams);
    if (mode === 'admin-template') return updateTemplateQuestion(answerParams);

    return updateQuestion(answerParams);
  };

  const onProvidedElsewhere = (): void => {
    const newAnswer: string = isProvidedElsewhere
      ? ''
      : PREDEFINED_PROVIDED_ELSEWHERE;
    const newQuestion: Question = { ...question, answer: newAnswer };

    updateAnswer(question.id, newAnswer);

    if (mode === 'admin-preview') {
      return setSelectedPreviewQuestion(newQuestion);
    }
    if (mode === 'admin-template') return;

    return setSelectedQuestion(newQuestion);
  };

  const onUndo = () => updateAnswer(question.id, '');

  const handleDownloadAttachment = async () => {
    try {
      const { document, error } = await downloadAttachedDocument({
        questionId: question.id,
        token,
      });

      if (error) {
        throw new Error(error);
      }

      globalThis.open(document.data.link);
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const handleYesToDocumentDeletionCheck = () => {
    const fileNames = getUploadedItems(question?.answer);

    for (const fileName of fileNames) {
      deletePath({
        companyId: companyId!,
        path: `/${fileName}`,
        token,
      });
    }

    if (fileNames.length > 0) {
      updateAnswer(question?.id, '');
    }

    if (isProvidingElsewhere) {
      onProvidedElsewhere();
    } else if (isUploadingLater) {
      setIsUploadLaterDialogOpen(true);
    }

    setIsProvidingElsewhere(false);
    setIsUploadingLater(false);
    setIsDocumentDeletionCheckDialogOpened(false);
  };

  const handleNoToDocumentDeletionCheck = () => {
    setIsProvidingElsewhere(false);
    setIsUploadingLater(false);
    setIsDocumentDeletionCheckDialogOpened(false);
  };

  const answerHasDocuments = () => {
    if (!question?.answer) return false;
    if (question?.answer_by) return false;
    if (question?.answer === PREDEFINED_PROVIDED_ELSEWHERE) return false;

    return getUploadedItems(question?.answer).length > 0;
  };

  const toggleOpenDocumentDeletionCheckDialog = (
    isForProvidedElsewhere: boolean,
  ) => {
    setIsProvidingElsewhere(isForProvidedElsewhere);
    setIsUploadingLater(!isForProvidedElsewhere);
    return setIsDocumentDeletionCheckDialogOpened(true);
  };

  const onUploadLater = (): void => {
    const hasDocuments = answerHasDocuments();

    if (hasDocuments) {
      return toggleOpenDocumentDeletionCheckDialog(false);
    }

    return setIsUploadLaterDialogOpen(true);
  };

  const handleProvidedElsewhere = (): void | Promise<void> => {
    const hasDocuments: boolean = answerHasDocuments();

    if (hasDocuments) {
      return toggleOpenDocumentDeletionCheckDialog(true);
    }

    return onProvidedElsewhere();
  };

  const onNotApplicableToggle = async () => {
    const answerParams = {
      questionId: question.id,
      answer: isNotApplicable ? '' : PREDEFINED_NOT_APPLICABLE,
    };

    if (mode === 'admin-preview') return updatePreviewQuestion(answerParams);
    if (mode === 'admin-template') return updateTemplateQuestion(answerParams);

    return updateQuestion(answerParams);
  };

  const isNotApplicable: boolean =
    question?.answer === PREDEFINED_NOT_APPLICABLE;

  return (
    <>
      <UploadLaterDialog
        hasDocuments={answerHasDocuments()}
        isOpen={isUploadLaterDialogOpen}
        mode={mode}
        isDisabled={isDisabled}
        isUploadLater={isUploadLater}
        onCloseDialog={() => setIsUploadLaterDialogOpen(false)}
        question={question}
      />
      {hasAttachedDocument && (
        <Flex>
          <Button
            intent="ghost"
            size="small"
            value={attached_document?.split?.('/')?.[2] || ''}
            onClick={handleDownloadAttachment}
          />
        </Flex>
      )}
      <Flex isColumn gap={6}>
        {isDocumentDeletionCheckDialogOpened && (
          <ConfirmRemoveDocument
            onCancel={handleNoToDocumentDeletionCheck}
            onConfirm={handleYesToDocumentDeletionCheck}
          />
        )}
        <ButtonGroup>
          {!question?.disable_not_applicable && (
            <Button
              iconLeft={isNotApplicable ? 'check-circle' : 'prohibit'}
              size="small"
              intent={isNotApplicable ? 'primary' : 'secondary'}
              value={isNotApplicable ? 'Make Applicable' : 'Not Applicable'}
              onClick={onNotApplicableToggle}
              isDisabled={isDisabled}
            />
          )}
          {(isUploadLater || isProvidedElsewhere) && (
            <Button
              size="small"
              intent="primary"
              value="Undo"
              onClick={onUndo}
              isDisabled={isDisabled}
            />
          )}
          {!isProvidedElsewhere && !preferences?.disable_provided_elsewhere && (
            <Button
              size="small"
              intent="secondary"
              isDisabled={isDisabled}
              isActive={isProvidedElsewhere}
              value="Provided Elsewhere"
              onClick={handleProvidedElsewhere}
            />
          )}
          {!isUploadLater && !question?.disable_answer_later && (
            <Tooltip isDisabled={!isPastDue}>
              <Tooltip.Content>
                <Text type="small" color="currentcolor">
                  Questionnaire is past the due date.
                </Text>
              </Tooltip.Content>
              <Tooltip.Trigger>
                <Button
                  size="small"
                  intent="secondary"
                  value="Upload Later"
                  isDisabled={isDisabled || isPastDue}
                  onClick={onUploadLater}
                />
              </Tooltip.Trigger>
            </Tooltip>
          )}
          <NotesButton
            question={question}
            mode={mode}
            isDisabled={isDisabled}
          />
        </ButtonGroup>
      </Flex>
    </>
  );
};
